// Admin Tasks view — a cross-project task browser with the same start / pause /
// complete flow the mobile app exposes to engineers, plus admin-only filters
// (assignment, engineer, project-wide search). Designed to mirror the mobile
// task card so a site manager can triage work the same way an engineer sees it.
//
// Data contract lives in backend/routes/admin_tasks.js — see api.ts for the
// client wrappers.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Search, Filter, Play, Pause, CheckCircle2, Clock, MapPin,
    RefreshCw, X, AlertTriangle, ChevronRight, ChevronDown, Layers,
    ClipboardList, PauseCircle, Target,
    FileText, HardHat, Package, ShoppingCart, ExternalLink,
    Plus, Trash2, Phone, Loader2,
    type LucideIcon,
} from 'lucide-react';
import {
    api,
    AdminTask,
    AdminTaskDetailResponse,
    AdminTaskFilters,
    AdminTaskHoldEpisode,
    AdminTaskIssue,
    AdminTaskLinkedDocument,
    AdminMaterialRequest,
    AdminMaterialRollupRow,
    AdminContractor,
    AdminAttendanceRow,
    MaterialRequestStatus,
    ProjectLibraryDocument,
    HoldReason,
    IssueConcernType,
    IssuePriority,
    IssueStatus,
    ISSUE_CONCERN_TYPES,
    ISSUE_PRIORITIES,
    resolvePublicFileUrl,
    TaskStatus,
    COMMON_MATERIAL_UOM,
} from '../../lib/api';
import { Project, TeamMember } from '../shared/types';
import HoldReasonModal from './HoldReasonModal';
import ProjectTreePanel, { TreeNodeFilters } from '../shared/ProjectTreePanel';
import {
    GROUP_BY_OPTIONS,
    GroupBy,
    TaskGroup,
    groupTasks,
    initialCollapsedKeys,
    resolveGroupBy,
} from './taskGrouping';

interface TasksViewProps {
    projects: Project[];
    selectedProjectId: string | null;
    onProjectChange: (id: string) => void;
    // Theme + global navigation are owned by the persistent <AppSidebar>.
    // The prop is passed through only so the view can branch on it if needed
    // (currently unused — dark styling is driven by Tailwind's `dark:` variant).
    theme?: 'light' | 'dark';
}

const PAGE_SIZE = 50;

const STATUS_OPTIONS: { value: '' | TaskStatus; label: string }[] = [
    { value: '',             label: 'All statuses' },
    { value: 'In Progress',  label: 'In Progress' },
    { value: 'Not Started',  label: 'Not Started' },
    { value: 'On Hold',      label: 'On Hold' },
    { value: 'Completed',    label: 'Completed' },
];

const SCOPE_OPTIONS: { value: 'all' | 'week' | 'month'; label: string }[] = [
    { value: 'all',   label: 'All time' },
    { value: 'week',  label: 'This week' },
    { value: 'month', label: 'This month' },
];

const ASSIGNMENT_OPTIONS: { value: 'any' | 'assigned' | 'unassigned'; label: string }[] = [
    { value: 'any',        label: 'Any' },
    { value: 'assigned',   label: 'Assigned' },
    { value: 'unassigned', label: 'Unassigned' },
];

export default function TasksView({
    projects,
    selectedProjectId,
    onProjectChange,
}: TasksViewProps) {
    // ----- filter state -----
    const [searchInput, setSearchInput]   = useState('');
    const [searchDebounced, setSearchDeb] = useState('');
    const [status, setStatus]             = useState<'' | TaskStatus>('');
    const [assigneeId, setAssigneeId]     = useState<string>('');      // team_member.id or ''
    const [assignment, setAssignment]     = useState<'any' | 'assigned' | 'unassigned'>('any');
    const [scope, setScope]               = useState<'all' | 'week' | 'month'>('all');
    const [page, setPage]                 = useState(0);

    // ----- data state -----
    const [tasks, setTasks]     = useState<AdminTask[]>([]);
    const [total, setTotal]     = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState<string | null>(null);
    const [team, setTeam]       = useState<TeamMember[]>([]);

    // ----- drawer state -----
    const [drawerTaskId, setDrawerTaskId] = useState<string | null>(null);
    const [detail, setDetail] = useState<AdminTaskDetailResponse | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [actionBusyId, setActionBusyId] = useState<string | null>(null);

    // ----- hold-reason modal state -----
    // When a user clicks Pause we stash the target task here and open the
    // modal; submitting it fires the actual adminSetTaskStatus(On Hold, ...)
    // call. Clicking Resume / Complete skips the modal entirely.
    const [holdTarget, setHoldTarget] = useState<AdminTask | null>(null);

    // ----- location tree state -----
    // The left sidebar hosts the same Building → Category → Subcategory →
    // Floor → Unit tree the Planning view uses. Picking any node collapses
    // into a TreeNodeFilters payload that we merge into adminListTasks.
    const [selectedNodeKey, setSelectedNodeKey]     = useState<string | null>(null);
    const [selectedNodeFilters, setSelectedNodeFilters] = useState<TreeNodeFilters>({});
    const [selectedNodePath, setSelectedNodePath]   = useState<string[]>(['All buildings']);

    // ----- grouping state -----
    // The flat task list can hide structure (which floor / unit / milestone a
    // task belongs to) even when the tree already narrowed it down. Grouping
    // the current page by one axis brings that shape back. User choice is
    // persisted so their preferred view survives reloads.
    const [groupBy, setGroupBy] = useState<GroupBy>(() => {
        const saved = typeof localStorage !== 'undefined'
            ? localStorage.getItem('tasksView.groupBy')
            : null;
        return (saved as GroupBy | null) ?? 'auto';
    });
    useEffect(() => {
        try { localStorage.setItem('tasksView.groupBy', groupBy); } catch { /* no-op */ }
    }, [groupBy]);

    // Collapsed-group state. Re-seeded whenever the shape of the groups
    // changes meaningfully (grouping axis or task set changes) so a new
    // crowded group auto-collapses instead of dumping 200 rows on screen.
    const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<Set<string>>(new Set());

    // Reset tree selection when the project changes — old ids mean nothing
    // in the new project's scope.
    useEffect(() => {
        setSelectedNodeKey(null);
        setSelectedNodeFilters({});
        setSelectedNodePath(['All buildings']);
    }, [selectedProjectId]);

    // ----- debounce search input -----
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => setSearchDeb(searchInput.trim()), 300);
        return () => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
        };
    }, [searchInput]);

    // ----- reset pagination when filter inputs change -----
    useEffect(() => {
        setPage(0);
    }, [searchDebounced, status, assigneeId, assignment, scope, selectedProjectId, selectedNodeKey]);

    // ----- load team list when project changes (for the assignee dropdown) -----
    useEffect(() => {
        if (!selectedProjectId) { setTeam([]); return; }
        api.fetchTeam(selectedProjectId)
           .then(setTeam)
           .catch(() => setTeam([]));     // non-fatal; dropdown just stays empty
    }, [selectedProjectId]);

    // ----- load tasks -----
    const loadTasks = useCallback(async () => {
        if (!selectedProjectId) return;
        setLoading(true);
        setError(null);

        try {
            const filters: AdminTaskFilters = {
                projectId: selectedProjectId,
                status:    status || undefined,
                assignee:  assigneeId ? Number(assigneeId) : undefined,
                // Only send `assignment` when no specific assignee is chosen — a
                // named engineer is a stronger signal than "any/assigned/unassigned".
                assignment: assigneeId ? undefined : assignment,
                search:    searchDebounced || undefined,
                scope,
                // Tree-node filters (optional). Merged in from the left panel;
                // each level narrows the query further.
                buildingId:    selectedNodeFilters.buildingId,
                categoryId:    selectedNodeFilters.categoryId,
                subcategoryId: selectedNodeFilters.subcategoryId,
                floorId:       selectedNodeFilters.floorId,
                unitId:        selectedNodeFilters.unitId,
                limit:  PAGE_SIZE,
                offset: page * PAGE_SIZE,
            };
            const res = await api.adminListTasks(filters);
            setTasks(res.tasks);
            setTotal(res.total);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Failed to load tasks';
            setError(msg);
            setTasks([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [
        selectedProjectId, status, assigneeId, assignment, searchDebounced, scope, page,
        selectedNodeFilters.buildingId, selectedNodeFilters.categoryId,
        selectedNodeFilters.subcategoryId, selectedNodeFilters.floorId, selectedNodeFilters.unitId,
    ]);

    useEffect(() => { loadTasks(); }, [loadTasks]);

    // ----- open / load drawer -----
    const openDrawer = useCallback(async (taskId: string) => {
        setDrawerTaskId(taskId);
        setDetail(null);
        setDetailLoading(true);
        try {
            const d = await api.adminGetTaskDetail(taskId);
            setDetail(d);
        } catch (err) {
            console.error(err);
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const closeDrawer = useCallback(() => {
        setDrawerTaskId(null);
        setDetail(null);
    }, []);

    // Lightweight refresher used by the drawer's inner tabs (Issues now,
    // Docs/Labour/Requests later) after they mutate sub-resources. We
    // skip the spinner so the tab swap feels instant; adminGetTaskDetail
    // already returns the merged task + history + issues + holdEpisodes.
    //
    // We also splice the refreshed task back into the list state so card
    // chips (open issues count, hold reason, etc.) stay in sync — the
    // drawer can't mutate `tasks` without a round-trip, so the detail
    // response doubles as a single-row list update.
    const reloadDetail = useCallback(async () => {
        if (!drawerTaskId) return;
        try {
            const d = await api.adminGetTaskDetail(drawerTaskId);
            setDetail(d);
            setTasks((prev) => prev.map((t) => (t.id === d.task.id ? d.task : t)));
        } catch (err) {
            console.error(err);
        }
    }, [drawerTaskId]);

    // ----- inline status changes (start / pause / complete) -----
    // Pause (On Hold) detours through the HoldReasonModal so we always capture
    // a reason. Start/Resume/Complete fire immediately.
    const commitStatusChange = useCallback(
        async (
            task: AdminTask,
            newStatus: 'In Progress' | 'On Hold' | 'Completed',
            holdReason?: HoldReason,
            holdNotes?: string,
        ) => {
            setActionBusyId(task.id);
            try {
                const res = await api.adminSetTaskStatus(task.id, newStatus, {
                    holdReason,
                    holdNotes,
                });
                if (res.task) {
                    setTasks((prev) => prev.map((t) => (t.id === task.id ? res.task! : t)));
                }
                if (drawerTaskId === task.id) {
                    const d = await api.adminGetTaskDetail(task.id);
                    setDetail(d);
                }
            } catch (err: unknown) {
                alert(err instanceof Error ? err.message : 'Failed to update task');
            } finally {
                setActionBusyId(null);
            }
        },
        [drawerTaskId],
    );

    const handleStatusChange = useCallback(
        (task: AdminTask, newStatus: 'In Progress' | 'On Hold' | 'Completed') => {
            if (newStatus === 'On Hold') {
                setHoldTarget(task);
                return;
            }
            void commitStatusChange(task, newStatus);
        },
        [commitStatusChange],
    );

    const handleHoldConfirm = useCallback(
        (reason: HoldReason, notes?: string) => {
            if (!holdTarget) return;
            const target = holdTarget;
            setHoldTarget(null);
            void commitStatusChange(target, 'On Hold', reason, notes);
        },
        [holdTarget, commitStatusChange],
    );

    const selectedProject = useMemo(
        () => projects.find((p) => p.id === selectedProjectId) || null,
        [projects, selectedProjectId],
    );

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const hasActiveFilters = Boolean(
        status || assigneeId || assignment !== 'any' || searchDebounced || scope !== 'all',
    );

    const clearFilters = () => {
        setStatus('');
        setAssigneeId('');
        setAssignment('any');
        setScope('all');
        setSearchInput('');
    };

    // Resolve 'auto' to a concrete axis based on the current tree selection.
    const effectiveGroupBy = useMemo(
        () => resolveGroupBy(groupBy, selectedNodeFilters),
        [groupBy, selectedNodeFilters],
    );

    // Build the visible groups. `effectiveGroupBy === 'none'` produces a
    // single synthetic group we won't render a header for, which keeps the
    // render path uniform.
    const groups = useMemo<TaskGroup[]>(
        () => (effectiveGroupBy === 'none' ? [] : groupTasks(tasks, effectiveGroupBy)),
        [tasks, effectiveGroupBy],
    );

    // Seed collapsed state whenever the set of group keys changes. We don't
    // wipe the user's manual toggles within the same key-set — only reshuffle
    // when the axis or the page's tasks shift.
    const groupKeySignature = groups.map((g) => g.key).join('|');
    const lastSignatureRef = useRef<string>('');
    useEffect(() => {
        if (lastSignatureRef.current === groupKeySignature) return;
        lastSignatureRef.current = groupKeySignature;
        setCollapsedGroupKeys(initialCollapsedKeys(groups));
    }, [groupKeySignature, groups]);

    const toggleGroup = (key: string) => {
        setCollapsedGroupKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };
    const expandAll   = () => setCollapsedGroupKeys(new Set());
    const collapseAll = () => setCollapsedGroupKeys(new Set(groups.map((g) => g.key)));

    return (
        <div className="h-screen flex bg-gray-100 dark:bg-slate-900 text-gray-900 dark:text-gray-100">
            {/* Sidebar: project picker on top, Building → Category → …
                navigation tree (shared with the Planning view) below. */}
            <ProjectTreePanel
                projectId={selectedProjectId}
                selectedKey={selectedNodeKey}
                onSelect={(key, filters, path) => {
                    setSelectedNodeKey(key);
                    setSelectedNodeFilters(filters);
                    setSelectedNodePath(path);
                }}
                className="w-72 shrink-0"
                header={
                    <>
                        <div className="h-16 flex items-center px-4 border-b border-gray-200 dark:border-slate-700">
                            <ClipboardList className="text-indigo-600 dark:text-indigo-400 mr-2" size={22} />
                            <span className="font-semibold text-lg">Tasks</span>
                        </div>
                        <div className="p-3">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Project
                            </label>
                            <select
                                value={selectedProjectId ?? ''}
                                onChange={(e) => onProjectChange(e.target.value)}
                                className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-md px-2 py-1.5 text-sm"
                            >
                                {projects.map((p) => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    </>
                }
            />

            {/* Main panel ---------------------------------------------------- */}
            <main className="flex-1 flex flex-col overflow-hidden">
                <header className="h-16 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between px-6 shadow-sm">
                    <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 min-w-0">
                        <span>{selectedProject?.name || 'Select a project'}</span>
                        <ChevronRight size={14} />
                        <span className="font-medium text-gray-900 dark:text-gray-100">Tasks</span>
                        {/* Selected-node breadcrumb — lets users see (and
                            clear) the active location filter without going
                            back to the tree. */}
                        {selectedNodeKey && selectedNodePath.length > 1 && (
                            <span className="ml-2 inline-flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-100 pl-2 pr-1 py-0.5 rounded-full max-w-[48ch]">
                                <MapPin size={11} />
                                <span className="truncate">
                                    {selectedNodePath.slice(1).join(' › ')}
                                </span>
                                <button
                                    onClick={() => {
                                        setSelectedNodeKey(null);
                                        setSelectedNodeFilters({});
                                        setSelectedNodePath(['All buildings']);
                                    }}
                                    className="p-0.5 rounded-full hover:bg-blue-200/70 dark:hover:bg-blue-800"
                                    title="Clear location filter"
                                >
                                    <X size={11} />
                                </button>
                            </span>
                        )}
                        {total > 0 && (
                            <span className="ml-3 text-xs bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                                {total.toLocaleString()} total
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={loadTasks}
                            className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                            title="Refresh"
                        >
                            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                </header>

                {/* Filter bar --------------------------------------------- */}
                <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-6 py-3 flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by task name or WBS code…"
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-md text-sm"
                        />
                    </div>

                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as '' | TaskStatus)}
                        className="border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-md px-2 py-2 text-sm"
                        title="Filter by status"
                    >
                        {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>

                    <select
                        value={assigneeId}
                        onChange={(e) => setAssigneeId(e.target.value)}
                        className="border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-md px-2 py-2 text-sm min-w-[160px]"
                        title="Filter by engineer"
                    >
                        <option value="">Any engineer</option>
                        {team.map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                    </select>

                    {!assigneeId && (
                        <select
                            value={assignment}
                            onChange={(e) => setAssignment(e.target.value as 'any' | 'assigned' | 'unassigned')}
                            className="border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-md px-2 py-2 text-sm"
                            title="Assignment state"
                        >
                            {ASSIGNMENT_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    )}

                    <select
                        value={scope}
                        onChange={(e) => setScope(e.target.value as 'all' | 'week' | 'month')}
                        className="border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-md px-2 py-2 text-sm"
                        title="Plan scope"
                    >
                        {SCOPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>

                    {hasActiveFilters && (
                        <button
                            onClick={clearFilters}
                            className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md"
                        >
                            <Filter size={14} />
                            <span>Clear filters</span>
                        </button>
                    )}

                    {/* Group-by selector — pushed to the right. Defaults to
                        'auto' which mirrors the next level down from the
                        current tree selection. */}
                    <div className="ml-auto flex items-center gap-2">
                        <Layers size={14} className="text-gray-400" />
                        <select
                            value={groupBy}
                            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                            className="border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-md px-2 py-2 text-sm"
                            title="Group tasks by"
                        >
                            {GROUP_BY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.value === 'auto'
                                        ? `Auto (${axisLabelFor(effectiveGroupBy)})`
                                        : `Group: ${o.label}`}
                                </option>
                            ))}
                        </select>
                        {groups.length > 1 && (
                            <>
                                <button
                                    onClick={expandAll}
                                    className="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                                    title="Expand all groups"
                                >
                                    Expand
                                </button>
                                <span className="text-xs text-gray-300">·</span>
                                <button
                                    onClick={collapseAll}
                                    className="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                                    title="Collapse all groups"
                                >
                                    Collapse
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Task list ----------------------------------------------- */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading && tasks.length === 0 ? (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">Loading tasks…</div>
                    ) : error ? (
                        <div className="text-center text-red-600 dark:text-red-400 py-12">
                            {error}
                            <button onClick={loadTasks} className="ml-3 underline">Retry</button>
                        </div>
                    ) : tasks.length === 0 ? (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            No tasks match these filters.
                        </div>
                    ) : effectiveGroupBy === 'none' ? (
                        <div className="space-y-3">
                            {tasks.map((t) => (
                                <TaskCard
                                    key={t.id}
                                    task={t}
                                    busy={actionBusyId === t.id}
                                    onOpen={() => openDrawer(t.id)}
                                    onStatusChange={handleStatusChange}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {groups.map((g) => {
                                const isCollapsed = collapsedGroupKeys.has(g.key);
                                return (
                                    <section key={g.key}>
                                        <TaskGroupHeader
                                            group={g}
                                            collapsed={isCollapsed}
                                            onToggle={() => toggleGroup(g.key)}
                                        />
                                        {!isCollapsed && (
                                            <div className="space-y-3 mt-2">
                                                {g.tasks.map((t) => (
                                                    <TaskCard
                                                        key={t.id}
                                                        task={t}
                                                        busy={actionBusyId === t.id}
                                                        onOpen={() => openDrawer(t.id)}
                                                        onStatusChange={handleStatusChange}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </section>
                                );
                            })}
                            {total > tasks.length && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 italic px-1">
                                    Group counts reflect the current page only.
                                    Narrow with the tree or filters to see every group fully.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Pagination --------------------------------------------- */}
                {total > PAGE_SIZE && (
                    <div className="border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3 flex items-center justify-between text-sm">
                        <div className="text-gray-600 dark:text-gray-400">
                            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                disabled={page === 0 || loading}
                                className="px-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span className="text-gray-600 dark:text-gray-400">
                                {page + 1} / {totalPages}
                            </span>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                disabled={page >= totalPages - 1 || loading}
                                className="px-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </main>

            {/* Drawer ------------------------------------------------------- */}
            {drawerTaskId && (
                <TaskDrawer
                    loading={detailLoading}
                    detail={detail}
                    busy={actionBusyId === drawerTaskId}
                    onClose={closeDrawer}
                    onStatusChange={handleStatusChange}
                    onReloadDetail={reloadDetail}
                />
            )}

            {/* Hold-reason modal ------------------------------------------- */}
            {holdTarget && (
                <HoldReasonModal
                    taskName={holdTarget.name}
                    busy={actionBusyId === holdTarget.id}
                    onCancel={() => setHoldTarget(null)}
                    onConfirm={handleHoldConfirm}
                />
            )}
        </div>
    );
}

// ======================================================================
// TaskGroupHeader — sticky header that fronts each group. Click toggles
// the collapsed state; status chip counts summarise the group at a glance.
// ======================================================================

// Labels for the "axis" suffix in the group header. Separate from the
// selector labels because we want the singular (lowercase) form here.
function axisLabelFor(axis: Exclude<GroupBy, 'auto'>): string {
    switch (axis) {
        case 'none':        return 'none';
        case 'milestone':   return 'milestone';
        case 'building':    return 'building';
        case 'category':    return 'category';
        case 'subCategory': return 'subcategory';
        case 'floor':       return 'floor';
        case 'unit':        return 'unit';
        case 'status':      return 'status';
        case 'engineer':    return 'engineer';
    }
}

function TaskGroupHeader({
    group,
    collapsed,
    onToggle,
}: {
    group: TaskGroup;
    collapsed: boolean;
    onToggle: () => void;
}) {
    const { counts } = group;
    return (
        <button
            onClick={onToggle}
            className="sticky top-0 z-10 w-full flex items-center gap-3 px-3 py-2
                       bg-gray-100 dark:bg-neutral-900
                       border border-gray-200 dark:border-neutral-800
                       rounded-md text-left
                       hover:bg-gray-200 dark:hover:bg-neutral-800 transition-colors"
            title={collapsed ? 'Expand group' : 'Collapse group'}
        >
            {collapsed
                ? <ChevronRight size={16} className="text-gray-500 dark:text-gray-400 shrink-0" />
                : <ChevronDown  size={16} className="text-gray-500 dark:text-gray-400 shrink-0" />}

            <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                {group.label}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                · {counts.total} {counts.total === 1 ? 'task' : 'tasks'}
            </span>

            <div className="ml-auto flex items-center gap-1.5 shrink-0">
                {counts.inProgress > 0 && (
                    <span className="px-1.5 py-0.5 rounded text-[11px] font-medium
                                     bg-gray-200 text-gray-700
                                     dark:bg-neutral-800 dark:text-neutral-200">
                        {counts.inProgress} in progress
                    </span>
                )}
                {counts.onHold > 0 && (
                    <span className="px-1.5 py-0.5 rounded text-[11px] font-medium
                                     bg-amber-100 text-amber-800
                                     dark:bg-amber-900/40 dark:text-amber-200">
                        {counts.onHold} on hold
                    </span>
                )}
                {counts.notStarted > 0 && (
                    <span className="px-1.5 py-0.5 rounded text-[11px] font-medium
                                     bg-gray-200 text-gray-700
                                     dark:bg-slate-700 dark:text-gray-300">
                        {counts.notStarted} not started
                    </span>
                )}
                {counts.completed > 0 && (
                    <span className="px-1.5 py-0.5 rounded text-[11px] font-medium
                                     bg-green-100 text-green-800
                                     dark:bg-green-900/40 dark:text-green-200">
                        {counts.completed} done
                    </span>
                )}
                {counts.openIssues > 0 && (
                    <span className="px-1.5 py-0.5 rounded text-[11px] font-medium
                                     bg-red-100 text-red-800
                                     dark:bg-red-900/40 dark:text-red-200
                                     flex items-center gap-1">
                        <AlertTriangle size={10} />
                        {counts.openIssues}
                    </span>
                )}
            </div>
        </button>
    );
}

// ======================================================================
// TaskCard — a single row in the admin task list. Compact but exposes
// everything a planner needs at a glance + the three primary actions.
// ======================================================================
function TaskCard({
    task,
    busy,
    onOpen,
    onStatusChange,
}: {
    task: AdminTask;
    busy: boolean;
    onOpen: () => void;
    onStatusChange: (t: AdminTask, s: 'In Progress' | 'On Hold' | 'Completed') => void;
}) {
    const statusStyle = getStatusStyle(task.status);
    const canStart    = task.status === 'Not Started' || task.status === 'On Hold';
    const canPause    = task.status === 'In Progress';
    const canComplete = task.status === 'In Progress' || task.status === 'On Hold';
    const locationCrumb = [task.building, task.floor, task.unit].filter(Boolean).join(' · ');

    return (
        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-4">
                <button onClick={onOpen} className="flex-1 min-w-0 text-left">
                    {locationCrumb && (
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-1">
                            <MapPin size={12} className="mr-1" />
                            <span className="truncate">{locationCrumb}</span>
                            {task.category && (
                                <>
                                    <span className="mx-1">·</span>
                                    <span className="truncate">{task.category}</span>
                                </>
                            )}
                        </div>
                    )}
                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{task.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                        {task.wbsCode && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate">{task.wbsCode}</div>
                        )}
                        {/* Milestone chip — surfaces the parent milestone the
                            ERP rolls this leaf up into (migration 008). */}
                        {task.milestoneName && (
                            <span
                                className="inline-flex items-center gap-1 text-[10px] bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-neutral-200 px-1.5 py-0.5 rounded-full max-w-[20ch] truncate"
                                title={`Milestone: ${task.milestoneWbsCode || ''} ${task.milestoneName}`}
                            >
                                <Target size={9} />
                                <span className="truncate">{task.milestoneName}</span>
                            </span>
                        )}
                    </div>
                </button>

                <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle}`}>
                        {task.status}
                    </span>
                    {task.status === 'On Hold' && task.currentHoldReason && (
                        <span
                            className="inline-flex items-center text-xs text-amber-700 dark:text-amber-300 bg-amber-100/70 dark:bg-amber-900/40 px-1.5 py-0.5 rounded"
                            title={task.currentHoldNotes || task.currentHoldReason}
                        >
                            <PauseCircle size={11} className="mr-0.5" />
                            {task.currentHoldReason}
                        </span>
                    )}
                    {/* Tab-status chips on the card — same visual language
                        as the drawer tabs so the listing hints at what the
                        planner will find inside. Future tabs (Docs, Labour,
                        Requests, Materials) should drop in here too. */}
                    <TabStatusChips task={task} />
                </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                <span className="flex items-center">
                    {task.assignedEngineerName ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-950 dark:bg-amber-950/70 dark:text-amber-100 px-2 py-0.5 text-xs font-semibold">
                            <span className="w-5 h-5 rounded-full bg-amber-600 text-white text-[10px] font-bold flex items-center justify-center">
                                {task.assignedEngineerName.split(/\s+/).filter(Boolean).map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                            </span>
                            {task.assignedEngineerName}
                        </span>
                    ) : (
                        <span className="italic text-gray-400">Unassigned</span>
                    )}
                </span>
                <span className="flex items-center">
                    <Clock size={12} className="mr-1" />
                    {formatDate(task.plannedStart)} → {formatDate(task.plannedFinish)}
                </span>
                {task.quantity > 0 && (
                    <span>
                        {task.quantityCompleted.toFixed(0)} / {task.quantity.toFixed(0)} {task.uom || ''}
                    </span>
                )}
            </div>

            <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-neutral-400 dark:bg-neutral-500"
                        style={{ width: `${Math.min(100, Math.max(0, task.progressPercent))}%` }}
                    />
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400 w-12 text-right">
                    {task.progressPercent}%
                </span>
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
                {canStart && (
                    <ActionButton
                        onClick={() => onStatusChange(task, 'In Progress')}
                        busy={busy}
                        color="emerald"
                        icon={<Play size={14} />}
                        label={task.status === 'On Hold' ? 'Resume' : 'Start'}
                    />
                )}
                {canPause && (
                    <ActionButton
                        onClick={() => onStatusChange(task, 'On Hold')}
                        busy={busy}
                        color="amber"
                        icon={<Pause size={14} />}
                        label="Pause"
                    />
                )}
                {canComplete && (
                    <ActionButton
                        onClick={() => onStatusChange(task, 'Completed')}
                        busy={busy}
                        color="indigo"
                        icon={<CheckCircle2 size={14} />}
                        label="Complete"
                    />
                )}
                <button
                    onClick={onOpen}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md"
                >
                    Details
                </button>
            </div>
        </div>
    );
}

function ActionButton({
    onClick, busy, color, icon, label,
}: {
    onClick: () => void;
    busy: boolean;
    color: 'emerald' | 'amber' | 'indigo';
    icon: React.ReactNode;
    label: string;
}) {
    const palette = {
        emerald: 'bg-emerald-600 hover:bg-emerald-700 text-white',
        amber:   'bg-amber-500 hover:bg-amber-600 text-white',
        indigo:  'bg-neutral-700 hover:bg-neutral-600 text-white',
    }[color];
    return (
        <button
            onClick={onClick}
            disabled={busy}
            className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-50 ${palette}`}
        >
            {icon}<span>{label}</span>
        </button>
    );
}

// ======================================================================
// TaskDrawer — slide-in detail with tabbed content
//
// Layout (mirrors the mobile TaskDetailsScreen tab strip):
//   Sticky header  → name, status chips, location, Start/Pause/Complete
//   Tab strip      → LOG · ISSUES · DOCS · LABOUR · REQUESTS · MATERIALS
//   Scroll area    → content for the active tab
//
// Tabs are added to the shell *disabled* until their backing tables +
// routes are built. That way the admin UI matches the mobile surface
// even before every workflow is persisted.
// ======================================================================
type DrawerTabKey = 'LOG' | 'ISSUES' | 'DOCS' | 'LABOUR' | 'REQUESTS' | 'MATERIALS';

interface DrawerTabDef {
    key: DrawerTabKey;
    label: string;
    icon: LucideIcon;
    enabled: boolean;
    badgeCount?: number;
}

function TaskDrawer({
    loading,
    detail,
    busy,
    onClose,
    onStatusChange,
    onReloadDetail,
}: {
    loading: boolean;
    detail: AdminTaskDetailResponse | null;
    busy: boolean;
    onClose: () => void;
    onStatusChange: (t: AdminTask, s: 'In Progress' | 'On Hold' | 'Completed') => void;
    onReloadDetail: () => Promise<void>;
}) {
    const [activeTab, setActiveTab] = useState<DrawerTabKey>('LOG');

    // Each tab grows into a real feature one at a time; `enabled=false` hides
    // the content behind a "Coming soon" placeholder so wiring up the next
    // one is an isolated change with no UI surprises.
    // Open-only count on the Issues tab — resolved/closed items shouldn't
    // keep pinging the planner after they've been actioned.
    const openIssueCount = (detail?.issues || []).filter(
        (i) => i.status === 'open' || i.status === 'acknowledged',
    ).length;
    const mandatoryDocCount = (detail?.documents || []).filter((d) => d.is_mandatory).length;
    const openMaterialRequestsCount = detail?.task.openMaterialRequestsCount ?? 0;
    const labourHeadcountToday = detail?.task.labourHeadcountToday ?? 0;

    const tabs: DrawerTabDef[] = [
        { key: 'LOG',       label: 'Log',       icon: ClipboardList, enabled: true },
        { key: 'ISSUES',    label: 'Issues',    icon: AlertTriangle, enabled: true, badgeCount: openIssueCount },
        {
            key: 'DOCS',
            label: 'Docs',
            icon: FileText,
            enabled: true,
            badgeCount: mandatoryDocCount > 0 ? mandatoryDocCount : undefined,
        },
        {
            key: 'LABOUR',
            label: 'Labour',
            icon: HardHat,
            enabled: true,
            badgeCount: labourHeadcountToday > 0 ? labourHeadcountToday : undefined,
        },
        {
            key: 'REQUESTS',
            label: 'Requests',
            icon: ShoppingCart,
            enabled: true,
            badgeCount: openMaterialRequestsCount > 0 ? openMaterialRequestsCount : undefined,
        },
        { key: 'MATERIALS', label: 'Materials', icon: Package, enabled: true },
    ];

    return (
        <>
            <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
            <aside className="fixed top-0 right-0 bottom-0 w-[480px] max-w-full bg-white dark:bg-slate-800 shadow-2xl z-50 flex flex-col">
                <header className="h-14 flex items-center justify-between px-4 border-b border-gray-200 dark:border-slate-700">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Task details</span>
                    <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700">
                        <X size={18} />
                    </button>
                </header>

                {loading || !detail ? (
                    <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
                        Loading…
                    </div>
                ) : (
                    <>
                        {/* Sticky summary — visible in every tab. */}
                        <div className="px-4 pt-4 pb-3 border-b border-gray-200 dark:border-slate-700 space-y-3">
                            <div>
                                <h3 className="font-semibold text-base text-gray-900 dark:text-gray-100">{detail.task.name}</h3>
                                {detail.task.wbsCode && (
                                    <div className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-0.5">
                                        {detail.task.wbsCode}
                                    </div>
                                )}
                                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(detail.task.status)}`}>
                                        {detail.task.status}
                                    </span>
                                    {detail.task.status === 'On Hold' && detail.task.currentHoldReason && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                            <PauseCircle size={11} className="mr-1" />
                                            {detail.task.currentHoldReason}
                                        </span>
                                    )}
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {[detail.task.building, detail.task.floor, detail.task.unit].filter(Boolean).join(' · ')}
                                    </span>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <ActionButton
                                    onClick={() => onStatusChange(detail.task, 'In Progress')}
                                    busy={busy}
                                    color="emerald"
                                    icon={<Play size={14} />}
                                    label={detail.task.status === 'On Hold' ? 'Resume' : 'Start'}
                                />
                                <ActionButton
                                    onClick={() => onStatusChange(detail.task, 'On Hold')}
                                    busy={busy}
                                    color="amber"
                                    icon={<Pause size={14} />}
                                    label="Pause"
                                />
                                <ActionButton
                                    onClick={() => onStatusChange(detail.task, 'Completed')}
                                    busy={busy}
                                    color="indigo"
                                    icon={<CheckCircle2 size={14} />}
                                    label="Complete"
                                />
                            </div>
                        </div>

                        <DrawerTabStrip
                            tabs={tabs}
                            activeTab={activeTab}
                            onChange={setActiveTab}
                        />

                        <div className="flex-1 overflow-y-auto p-4 space-y-5">
                            {activeTab === 'LOG'    && <LogTabContent detail={detail} />}
                            {activeTab === 'ISSUES' && (
                                <IssuesTabContent detail={detail} onReload={onReloadDetail} />
                            )}
                            {activeTab === 'DOCS'     && (
                                <DocsTabContent detail={detail} onReload={onReloadDetail} />
                            )}
                            {activeTab === 'LABOUR'   && (
                                <LabourTabContent taskId={detail.task.id} onReload={onReloadDetail} />
                            )}
                            {activeTab === 'REQUESTS' && (
                                <RequestsTabContent taskId={detail.task.id} onReload={onReloadDetail} />
                            )}
                            {activeTab === 'MATERIALS' && (
                                <MaterialsTabContent taskId={detail.task.id} />
                            )}
                            {!tabs.find((t) => t.key === activeTab)?.enabled && (
                                <ComingSoonPlaceholder tab={activeTab} />
                            )}
                        </div>
                    </>
                )}
            </aside>
        </>
    );
}

const DOC_MATCH_ORDER: Record<AdminTaskLinkedDocument['match_type'], number> = {
    direct: 0,
    building: 1,
    floor: 2,
    category: 3,
    project: 4,
};

function matchTypeLabel(m: AdminTaskLinkedDocument['match_type']): string {
    switch (m) {
        case 'direct':
            return 'Linked to task';
        case 'floor':
            return 'Floor';
        case 'category':
            return 'Category';
        case 'building':
            return 'Building';
        case 'project':
            return 'Project';
        default:
            return m;
    }
}

type DocFilter = 'all' | 'drawing' | 'sop' | 'other';

function sortDocs(list: AdminTaskLinkedDocument[]) {
    return [...list].sort((a, b) => {
        const oa = DOC_MATCH_ORDER[a.match_type] ?? 99;
        const ob = DOC_MATCH_ORDER[b.match_type] ?? 99;
        if (oa !== ob) return oa - ob;
        return a.title.localeCompare(b.title);
    });
}

function docMatchesFilter(d: AdminTaskLinkedDocument, f: DocFilter): boolean {
    if (f === 'all') return true;
    if (f === 'drawing') return d.document_type === 'drawing';
    if (f === 'sop') return d.document_type === 'sop';
    return d.document_type !== 'drawing' && d.document_type !== 'sop';
}

function DocsTabContent({
    detail,
    onReload,
}: {
    detail: AdminTaskDetailResponse;
    onReload: () => Promise<void>;
}) {
    const documents = detail.documents ?? [];
    const [docFilter, setDocFilter] = useState<DocFilter>('all');
    const [library, setLibrary] = useState<ProjectLibraryDocument[]>([]);
    const [libraryLoading, setLibraryLoading] = useState(false);
    const [pickDocId, setPickDocId] = useState('');
    const [linkMandatory, setLinkMandatory] = useState(false);
    const [linkBusy, setLinkBusy] = useState(false);

    const filtered = useMemo(
        () => documents.filter((d) => docMatchesFilter(d, docFilter)),
        [documents, docFilter],
    );
    const attached = useMemo(() => sortDocs(filtered.filter((d) => d.match_type === 'direct')), [filtered]);
    const inherited = useMemo(() => sortDocs(filtered.filter((d) => d.match_type !== 'direct')), [filtered]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLibraryLoading(true);
            try {
                const rows = await api.fetchProjectDocuments(detail.task.projectId);
                if (!cancelled) setLibrary(rows);
            } catch {
                if (!cancelled) setLibrary([]);
            } finally {
                if (!cancelled) setLibraryLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [detail.task.projectId]);

    const linkDocument = async () => {
        if (!pickDocId) return;
        setLinkBusy(true);
        try {
            await api.linkDocumentToTask(pickDocId, detail.task.id, linkMandatory);
            setPickDocId('');
            setLinkMandatory(false);
            await onReload();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Link failed');
        } finally {
            setLinkBusy(false);
        }
    };

    const unlink = async (documentId: string) => {
        if (!confirm('Remove this attachment from the task?')) return;
        try {
            await api.adminUnlinkTaskDocument(detail.task.id, documentId);
            await onReload();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Unlink failed');
        }
    };

    const renderDocCard = (d: AdminTaskLinkedDocument, showUnlink: boolean) => {
        const href = resolvePublicFileUrl(d.file_path);
        const shortId = d.document_id.replace(/-/g, '').slice(0, 12);
        return (
            <li
                key={`${d.document_id}-${d.match_type}`}
                className="text-xs border border-gray-200 dark:border-slate-700 rounded-md p-2.5"
            >
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-800 dark:text-gray-100">
                            {d.title}
                        </div>
                        <div className="mt-0.5 font-mono text-[10px] text-gray-400 dark:text-gray-500">
                            Doc #{shortId}… · {matchTypeLabel(d.match_type)}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                                {d.document_type}
                            </span>
                            {d.category_name && (
                                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                    {d.category_name}
                                </span>
                            )}
                            {d.is_mandatory && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 font-medium">
                                    Required
                                </span>
                            )}
                        </div>
                        {(d.document_number || d.revision) && (
                            <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                {[d.document_number, d.revision ? `rev ${d.revision}` : null]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </div>
                        )}
                        <div className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500 truncate" title={d.file_name}>
                            {d.file_name}
                            {d.file_type ? ` · ${d.file_type}` : ''}
                        </div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1">
                        {href ? (
                            <a
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-0.5 px-2 py-1 rounded bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 text-[11px] font-medium"
                            >
                                <ExternalLink size={12} />
                                Open
                            </a>
                        ) : (
                            <span className="text-[10px] text-gray-400">No file</span>
                        )}
                        {showUnlink && (
                            <button
                                type="button"
                                onClick={() => unlink(d.document_id)}
                                className="text-[10px] text-red-600 dark:text-red-400 hover:underline"
                            >
                                Remove link
                            </button>
                        )}
                    </div>
                </div>
            </li>
        );
    };

    const filterPill = (key: DocFilter, label: string) => (
        <button
            key={key}
            type="button"
            onClick={() => setDocFilter(key)}
            className={`px-2 py-1 rounded text-[11px] font-medium ${
                docFilter === key
                    ? 'bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300'
            }`}
        >
            {label}
        </button>
    );

    return (
        <div className="space-y-4">
            <div>
                <div className="text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1.5">
                    Link from project library
                </div>
                <div className="flex flex-col gap-2 rounded-md border border-gray-200 dark:border-slate-700 p-2 bg-gray-50 dark:bg-slate-900/30">
                    {libraryLoading ? (
                        <div className="flex items-center gap-2 text-[11px] text-gray-500">
                            <Loader2 size={14} className="animate-spin" /> Loading documents…
                        </div>
                    ) : (
                        <>
                            <select
                                value={pickDocId}
                                onChange={(e) => setPickDocId(e.target.value)}
                                className="text-xs rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5"
                            >
                                <option value="">Choose a document…</option>
                                {library.map((row) => (
                                    <option key={row.id} value={row.id}>
                                        {row.title}
                                        {row.document_number ? ` (${row.document_number})` : ''} · {row.document_type}
                                    </option>
                                ))}
                            </select>
                            <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={linkMandatory}
                                    onChange={(e) => setLinkMandatory(e.target.checked)}
                                />
                                Mark as required reading
                            </label>
                            <button
                                type="button"
                                disabled={!pickDocId || linkBusy}
                                onClick={() => void linkDocument()}
                                className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium"
                            >
                                {linkBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                Attach to task
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="inline-flex rounded-md bg-gray-100 dark:bg-slate-700 p-0.5 flex-wrap gap-0.5">
                {filterPill('all', 'All')}
                {filterPill('drawing', 'Drawings')}
                {filterPill('sop', 'SOPs')}
                {filterPill('other', 'Other')}
            </div>

            {filtered.length === 0 ? (
                <div className="text-xs text-gray-500 dark:text-gray-400 py-6 text-center px-2">
                    No documents in this filter. Attach one above or scope documents to this task&apos;s
                    building, floor, or category in the project library.
                </div>
            ) : (
                <div className="space-y-4">
                    <section>
                        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                            Attached to this task ({attached.length})
                        </h4>
                        {attached.length === 0 ? (
                            <p className="text-[11px] text-gray-400">None yet — use the form above.</p>
                        ) : (
                            <ul className="space-y-2">{attached.map((d) => renderDocCard(d, true))}</ul>
                        )}
                    </section>
                    <section>
                        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                            Inherited from site / project ({inherited.length})
                        </h4>
                        {inherited.length === 0 ? (
                            <p className="text-[11px] text-gray-400">No scoped documents match this task.</p>
                        ) : (
                            <ul className="space-y-2">{inherited.map((d) => renderDocCard(d, false))}</ul>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
}

function LabourTabContent({
    taskId,
    onReload,
}: {
    taskId: string;
    onReload: () => Promise<void>;
}) {
    const [sub, setSub] = useState<'CONTRACTOR' | 'WORKERS'>('CONTRACTOR');
    const [contractors, setContractors] = useState<AdminContractor[]>([]);
    const [attendance, setAttendance] = useState<AdminAttendanceRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [scope, setScope] = useState('');
    const [addBusy, setAddBusy] = useState(false);
    const [attForm, setAttForm] = useState({
        contractor_id: '',
        attendance_date: new Date().toISOString().slice(0, 10),
        skilled_count: 0,
        unskilled_count: 0,
        notes: '',
    });
    const [attBusy, setAttBusy] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [c, a] = await Promise.all([
                api.adminListTaskContractors(taskId),
                api.adminListTaskAttendance(taskId),
            ]);
            setContractors(c);
            setAttendance(a);
        } catch {
            setContractors([]);
            setAttendance([]);
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    useEffect(() => { void refresh(); }, [refresh]);

    const addContractor = async () => {
        if (!name.trim()) return;
        setAddBusy(true);
        try {
            await api.adminAddTaskContractor(taskId, { name: name.trim(), phone: phone || undefined, scope: scope || undefined });
            setName('');
            setPhone('');
            setScope('');
            await refresh();
            await onReload();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Failed');
        } finally {
            setAddBusy(false);
        }
    };

    const removeContractor = async (id: string) => {
        if (!confirm('Remove this contractor from the task?')) return;
        try {
            await api.adminRemoveTaskContractor(taskId, id);
            await refresh();
            await onReload();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Failed');
        }
    };

    const saveAttendance = async () => {
        if (!attForm.contractor_id) {
            alert('Pick a contractor');
            return;
        }
        setAttBusy(true);
        try {
            await api.adminUpsertTaskAttendance(taskId, {
                contractor_id: attForm.contractor_id,
                attendance_date: attForm.attendance_date,
                skilled_count: attForm.skilled_count,
                unskilled_count: attForm.unskilled_count,
                notes: attForm.notes || undefined,
            });
            await refresh();
            await onReload();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Failed');
        } finally {
            setAttBusy(false);
        }
    };

    const deleteAtt = async (id: string) => {
        if (!confirm('Delete this attendance row?')) return;
        try {
            await api.adminDeleteAttendance(id);
            await refresh();
            await onReload();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Failed');
        }
    };

    const groupedAtt = useMemo(() => {
        const m = new Map<string, AdminAttendanceRow[]>();
        for (const r of attendance) {
            const k = r.attendance_date;
            if (!m.has(k)) m.set(k, []);
            m.get(k)!.push(r);
        }
        return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
    }, [attendance]);

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-8 justify-center">
                <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
        );
    }

    return (
        <>
            <div className="inline-flex rounded-md bg-gray-100 dark:bg-slate-700 p-0.5 text-xs">
                <button
                    type="button"
                    onClick={() => setSub('CONTRACTOR')}
                    className={`px-2.5 py-1 rounded ${sub === 'CONTRACTOR' ? 'bg-white dark:bg-slate-800 shadow-sm' : ''}`}
                >
                    Contractors
                </button>
                <button
                    type="button"
                    onClick={() => setSub('WORKERS')}
                    className={`px-2.5 py-1 rounded ${sub === 'WORKERS' ? 'bg-white dark:bg-slate-800 shadow-sm' : ''}`}
                >
                    Attendance
                </button>
            </div>

            {sub === 'CONTRACTOR' ? (
                <div className="space-y-3">
                    <div className="rounded-md border border-gray-200 dark:border-slate-700 p-2 space-y-2">
                        <div className="text-[11px] font-medium text-gray-600 dark:text-gray-300">Add contractor</div>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Name *"
                            className="w-full text-xs rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5"
                        />
                        <input
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="Phone"
                            className="w-full text-xs rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5"
                        />
                        <input
                            value={scope}
                            onChange={(e) => setScope(e.target.value)}
                            placeholder="Scope (e.g. masonry)"
                            className="w-full text-xs rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5"
                        />
                        <button
                            type="button"
                            disabled={addBusy}
                            onClick={() => void addContractor()}
                            className="w-full py-1.5 rounded bg-indigo-600 text-white text-xs font-medium disabled:opacity-50"
                        >
                            {addBusy ? 'Saving…' : 'Add / link'}
                        </button>
                    </div>
                    <ul className="space-y-2">
                        {contractors.length === 0 ? (
                            <li className="text-xs text-gray-400">No contractors linked.</li>
                        ) : (
                            contractors.map((c) => (
                                <li
                                    key={c.id}
                                    className="flex items-start justify-between gap-2 text-xs border border-gray-200 dark:border-slate-700 rounded p-2"
                                >
                                    <div>
                                        <div className="font-medium text-gray-800 dark:text-gray-100">{c.name}</div>
                                        {c.scope && <div className="text-gray-500">{c.scope}</div>}
                                        {c.phone && (
                                            <a href={`tel:${c.phone}`} className="inline-flex items-center gap-0.5 text-indigo-600 dark:text-indigo-400 mt-0.5">
                                                <Phone size={11} /> {c.phone}
                                            </a>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => void removeContractor(c.id)}
                                        className="p-1 text-gray-400 hover:text-red-600"
                                        title="Remove"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="rounded-md border border-gray-200 dark:border-slate-700 p-2 space-y-2">
                        <div className="text-[11px] font-medium text-gray-600 dark:text-gray-300">Log attendance</div>
                        <select
                            value={attForm.contractor_id}
                            onChange={(e) => setAttForm((f) => ({ ...f, contractor_id: e.target.value }))}
                            className="w-full text-xs rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5"
                        >
                            <option value="">Contractor…</option>
                            {contractors.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <input
                            type="date"
                            value={attForm.attendance_date}
                            onChange={(e) => setAttForm((f) => ({ ...f, attendance_date: e.target.value }))}
                            className="w-full text-xs rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5"
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <label className="text-[11px] text-gray-600 dark:text-gray-300">
                                Skilled
                                <input
                                    type="number"
                                    min={0}
                                    value={attForm.skilled_count}
                                    onChange={(e) => setAttForm((f) => ({ ...f, skilled_count: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                                    className="mt-0.5 w-full text-xs rounded border border-gray-300 dark:border-slate-600 px-2 py-1"
                                />
                            </label>
                            <label className="text-[11px] text-gray-600 dark:text-gray-300">
                                Unskilled
                                <input
                                    type="number"
                                    min={0}
                                    value={attForm.unskilled_count}
                                    onChange={(e) => setAttForm((f) => ({ ...f, unskilled_count: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                                    className="mt-0.5 w-full text-xs rounded border border-gray-300 dark:border-slate-600 px-2 py-1"
                                />
                            </label>
                        </div>
                        <input
                            value={attForm.notes}
                            onChange={(e) => setAttForm((f) => ({ ...f, notes: e.target.value }))}
                            placeholder="Notes (optional)"
                            className="w-full text-xs rounded border border-gray-300 dark:border-slate-600 px-2 py-1.5"
                        />
                        <button
                            type="button"
                            disabled={attBusy || contractors.length === 0}
                            onClick={() => void saveAttendance()}
                            className="w-full py-1.5 rounded bg-indigo-600 text-white text-xs font-medium disabled:opacity-50"
                        >
                            {attBusy ? 'Saving…' : 'Save row'}
                        </button>
                    </div>
                    <div className="space-y-3">
                        {groupedAtt.length === 0 ? (
                            <p className="text-xs text-gray-400">No attendance logged (last 30 days).</p>
                        ) : (
                            groupedAtt.map(([date, rows]) => (
                                <div key={date}>
                                    <div className="text-[11px] font-semibold text-gray-500 mb-1">{date}</div>
                                    <ul className="space-y-1">
                                        {rows.map((r) => (
                                            <li
                                                key={r.id}
                                                className="flex items-center justify-between text-xs border border-gray-200 dark:border-slate-700 rounded px-2 py-1"
                                            >
                                                <span>
                                                    {r.contractor_name}: {r.skilled_count} skilled, {r.unskilled_count} unskilled
                                                </span>
                                                <button type="button" onClick={() => void deleteAtt(r.id)} className="text-gray-400 hover:text-red-600 p-0.5">
                                                    <Trash2 size={12} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

function RequestsTabContent({
    taskId,
    onReload,
}: {
    taskId: string;
    onReload: () => Promise<void>;
}) {
    const [requests, setRequests] = useState<AdminMaterialRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [bucket, setBucket] = useState<'open' | 'closed'>('open');
    const [showCreate, setShowCreate] = useState(false);
    const [issueFor, setIssueFor] = useState<AdminMaterialRequest | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await api.adminListMaterialRequests(taskId);
            setRequests(rows);
        } catch {
            setRequests([]);
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    useEffect(() => { void load(); }, [load]);

    const isOpenStatus = (s: MaterialRequestStatus) =>
        s === 'requested' || s === 'approved' || s === 'issued';
    const visible = requests.filter((r) => (bucket === 'open' ? isOpenStatus(r.status) : !isOpenStatus(r.status)));

    const transition = async (id: string, status: MaterialRequestStatus, extra?: { bill_no?: string; items?: { id: string; quantity_issued: number }[] }) => {
        setBusyId(id);
        try {
            await api.adminPatchMaterialRequest(id, { status, ...extra });
            await load();
            await onReload();
            setIssueFor(null);
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Update failed');
        } finally {
            setBusyId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-8 justify-center">
                <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center justify-between gap-2">
                <div className="inline-flex rounded-md bg-gray-100 dark:bg-slate-700 p-0.5 text-xs">
                    <button
                        type="button"
                        onClick={() => setBucket('open')}
                        className={`px-2.5 py-1 rounded ${bucket === 'open' ? 'bg-white dark:bg-slate-800 shadow-sm' : ''}`}
                    >
                        Open
                    </button>
                    <button
                        type="button"
                        onClick={() => setBucket('closed')}
                        className={`px-2.5 py-1 rounded ${bucket === 'closed' ? 'bg-white dark:bg-slate-800 shadow-sm' : ''}`}
                    >
                        Closed
                    </button>
                </div>
                <button
                    type="button"
                    onClick={() => setShowCreate(true)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-600 text-white text-xs font-medium"
                >
                    <Plus size={12} /> New request
                </button>
            </div>
            <div className="mt-3 space-y-2">
                {visible.length === 0 ? (
                    <p className="text-xs text-gray-400 py-6 text-center">No requests in this view.</p>
                ) : (
                    visible.map((r) => (
                        <MaterialRequestCard
                            key={r.id}
                            request={r}
                            busy={busyId === r.id}
                            onApprove={() => void transition(r.id, 'approved')}
                            onCancel={() => void transition(r.id, 'cancelled')}
                            onIssue={() => setIssueFor(r)}
                            onReceive={() => void transition(r.id, 'received')}
                            onDelete={async () => {
                                if (!confirm('Delete this draft request?')) return;
                                setBusyId(r.id);
                                try {
                                    await api.adminDeleteMaterialRequest(r.id);
                                    await load();
                                    await onReload();
                                } catch (e) {
                                    alert(e instanceof Error ? e.message : 'Delete failed');
                                } finally {
                                    setBusyId(null);
                                }
                            }}
                        />
                    ))
                )}
            </div>
            {showCreate && (
                <CreateMaterialRequestModal
                    taskId={taskId}
                    onClose={() => setShowCreate(false)}
                    onCreated={async () => {
                        setShowCreate(false);
                        await load();
                        await onReload();
                    }}
                />
            )}
            {issueFor && (
                <IssueMaterialModal
                    request={issueFor}
                    onClose={() => setIssueFor(null)}
                    onSubmit={(bill_no, items) => void transition(issueFor.id, 'issued', { bill_no, items })}
                    busy={busyId === issueFor.id}
                />
            )}
        </>
    );
}

function statusChipClass(s: MaterialRequestStatus): string {
    switch (s) {
        case 'requested': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
        case 'approved':  return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
        case 'issued':    return 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200';
        case 'received':  return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
        case 'cancelled': return 'bg-gray-200 text-gray-700 dark:bg-slate-600 dark:text-gray-200';
        default:          return 'bg-gray-100 text-gray-700';
    }
}

function MaterialRequestCard({
    request: r,
    busy,
    onApprove,
    onCancel,
    onIssue,
    onReceive,
    onDelete,
}: {
    request: AdminMaterialRequest;
    busy: boolean;
    onApprove: () => void;
    onCancel: () => void;
    onIssue: () => void;
    onReceive: () => void;
    onDelete: () => void;
}) {
    return (
        <div className="text-xs border border-gray-200 dark:border-slate-700 rounded-md p-2.5 space-y-2">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="font-mono font-medium text-gray-900 dark:text-gray-100">{r.request_code}</div>
                    <span className={`inline-flex mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${statusChipClass(r.status)}`}>
                        {r.status}
                    </span>
                    {r.bill_no && <div className="text-[11px] text-gray-500 mt-1">Bill: {r.bill_no}</div>}
                    {r.required_by && <div className="text-[11px] text-gray-500">Need by: {r.required_by}</div>}
                </div>
                <div className="flex flex-wrap gap-1 justify-end">
                    {r.status === 'requested' && (
                        <>
                            <button type="button" disabled={busy} onClick={onApprove} className="px-2 py-0.5 rounded bg-blue-600 text-white text-[10px]">Approve</button>
                            <button type="button" disabled={busy} onClick={onCancel} className="px-2 py-0.5 rounded bg-gray-200 dark:bg-slate-600 text-[10px]">Cancel</button>
                            <button type="button" disabled={busy} onClick={onDelete} className="px-2 py-0.5 rounded bg-gray-200 dark:bg-slate-600 text-[10px]">Delete</button>
                        </>
                    )}
                    {r.status === 'approved' && (
                        <>
                            <button type="button" disabled={busy} onClick={onIssue} className="px-2 py-0.5 rounded bg-violet-600 text-white text-[10px]">Issue</button>
                            <button type="button" disabled={busy} onClick={onCancel} className="px-2 py-0.5 rounded bg-gray-200 text-[10px]">Cancel</button>
                        </>
                    )}
                    {r.status === 'issued' && (
                        <>
                            <button type="button" disabled={busy} onClick={onReceive} className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px]">Received</button>
                            <button type="button" disabled={busy} onClick={onCancel} className="px-2 py-0.5 rounded bg-gray-200 text-[10px]">Cancel</button>
                        </>
                    )}
                </div>
            </div>
            {r.notes && <div className="text-gray-500">{r.notes}</div>}
            <table className="w-full text-[11px]">
                <tbody>
                    {r.items.map((it) => (
                        <tr key={it.id} className="border-t border-gray-100 dark:border-slate-700">
                            <td className="py-1 pr-2">{it.material_name}</td>
                            <td className="py-1 text-right text-gray-500">
                                {it.quantity} {it.uom}
                                {r.status !== 'requested' && r.status !== 'approved' && (
                                    <span className="ml-1">(issued {it.quantity_issued})</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CreateMaterialRequestModal({
    taskId,
    onClose,
    onCreated,
}: {
    taskId: string;
    onClose: () => void;
    onCreated: () => Promise<void>;
}) {
    const [requiredBy, setRequiredBy] = useState('');
    const [notes, setNotes] = useState('');
    const [lines, setLines] = useState([{ material_name: '', quantity: 1, uom: 'bags', notes: '' }]);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        const items = lines
            .filter((l) => l.material_name.trim())
            .map((l) => ({
                material_name: l.material_name.trim(),
                quantity: Number(l.quantity) || 0,
                uom: l.uom.trim() || 'nos',
                notes: l.notes || undefined,
            }));
        if (items.length === 0 || items.some((i) => i.quantity <= 0)) {
            alert('Add at least one line with quantity greater than 0');
            return;
        }
        setBusy(true);
        try {
            await api.adminCreateMaterialRequest(taskId, {
                required_by: requiredBy || null,
                notes: notes || null,
                items,
            });
            await onCreated();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4 space-y-3">
                <div className="flex justify-between items-center">
                    <h3 className="text-sm font-semibold">New material request</h3>
                    <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700"><X size={16} /></button>
                </div>
                <input type="date" value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)} className="w-full text-xs border rounded px-2 py-1.5 dark:bg-slate-900" />
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" className="w-full text-xs border rounded px-2 py-1.5 dark:bg-slate-900 min-h-[50px]" />
                {lines.map((line, i) => (
                    <div key={i} className="border border-gray-200 dark:border-slate-600 rounded p-2 space-y-1">
                        <input
                            value={line.material_name}
                            onChange={(e) => {
                                const next = [...lines];
                                next[i] = { ...next[i], material_name: e.target.value };
                                setLines(next);
                            }}
                            placeholder="Material"
                            className="w-full text-xs border rounded px-2 py-1 dark:bg-slate-900"
                        />
                        <div className="flex gap-1">
                            <input
                                type="number"
                                value={line.quantity}
                                onChange={(e) => {
                                    const next = [...lines];
                                    next[i] = { ...next[i], quantity: parseFloat(e.target.value) || 0 };
                                    setLines(next);
                                }}
                                className="w-20 text-xs border rounded px-2 py-1 dark:bg-slate-900"
                            />
                            <select
                                value={line.uom}
                                onChange={(e) => {
                                    const next = [...lines];
                                    next[i] = { ...next[i], uom: e.target.value };
                                    setLines(next);
                                }}
                                className="flex-1 text-xs border rounded px-2 py-1 dark:bg-slate-900"
                            >
                                {COMMON_MATERIAL_UOM.map((u) => (
                                    <option key={u} value={u}>{u}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={() => setLines([...lines, { material_name: '', quantity: 1, uom: 'bags', notes: '' }])}
                    className="text-xs text-indigo-600"
                >
                    + Line
                </button>
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={onClose} className="flex-1 py-1.5 rounded border text-xs">Cancel</button>
                    <button type="button" disabled={busy} onClick={() => void submit()} className="flex-1 py-1.5 rounded bg-indigo-600 text-white text-xs">
                        {busy ? 'Saving…' : 'Submit'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function IssueMaterialModal({
    request,
    onClose,
    onSubmit,
    busy,
}: {
    request: AdminMaterialRequest;
    onClose: () => void;
    onSubmit: (bill_no: string, items: { id: string; quantity_issued: number }[]) => void;
    busy: boolean;
}) {
    const [billNo, setBillNo] = useState('');
    const [qty, setQty] = useState<Record<string, number>>(() =>
        Object.fromEntries(request.items.map((it) => [it.id, Number(it.quantity)])),
    );

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-4 space-y-3">
                <div className="flex justify-between items-center">
                    <h3 className="text-sm font-semibold">Issue {request.request_code}</h3>
                    <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700"><X size={16} /></button>
                </div>
                <label className="block text-xs">
                    Bill / gate pass #
                    <input value={billNo} onChange={(e) => setBillNo(e.target.value)} className="mt-0.5 w-full border rounded px-2 py-1 dark:bg-slate-900" />
                </label>
                {request.items.map((it) => (
                    <label key={it.id} className="block text-xs">
                        {it.material_name} (max {it.quantity} {it.uom})
                        <input
                            type="number"
                            value={qty[it.id] ?? 0}
                            min={0}
                            max={Number(it.quantity)}
                            onChange={(e) => setQty((q) => ({ ...q, [it.id]: parseFloat(e.target.value) || 0 }))}
                            className="mt-0.5 w-full border rounded px-2 py-1 dark:bg-slate-900"
                        />
                    </label>
                ))}
                <div className="flex gap-2 pt-2">
                    <button type="button" onClick={onClose} className="flex-1 py-1.5 rounded border text-xs">Back</button>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => onSubmit(billNo, request.items.map((it) => ({ id: it.id, quantity_issued: qty[it.id] ?? 0 })))}
                        className="flex-1 py-1.5 rounded bg-violet-600 text-white text-xs"
                    >
                        {busy ? '…' : 'Confirm issue'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function MaterialsTabContent({ taskId }: { taskId: string }) {
    const [rows, setRows] = useState<AdminMaterialRollupRow[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await api.adminListTaskMaterialsRollup(taskId);
                if (!cancelled) setRows(r);
            } catch {
                if (!cancelled) setRows([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [taskId]);

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-8 justify-center">
                <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
        );
    }
    if (rows.length === 0) {
        return <p className="text-xs text-gray-400 py-6 text-center">No issued materials yet for this task.</p>;
    }
    return (
        <table className="w-full text-xs">
            <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200 dark:border-slate-700">
                    <th className="py-2 pr-2">Material</th>
                    <th className="py-2 pr-2">Requested</th>
                    <th className="py-2">Issued</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={`${r.material_name}-${r.uom}`} className="border-b border-gray-100 dark:border-slate-700">
                        <td className="py-2 pr-2">{r.material_name}</td>
                        <td className="py-2 pr-2 text-gray-600">{r.quantity_requested} {r.uom}</td>
                        <td className="py-2">{r.quantity_issued} {r.uom}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// ---- drawer tab strip ------------------------------------------------
function DrawerTabStrip({
    tabs,
    activeTab,
    onChange,
}: {
    tabs: DrawerTabDef[];
    activeTab: DrawerTabKey;
    onChange: (k: DrawerTabKey) => void;
}) {
    return (
        <div className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40">
            <div className="flex overflow-x-auto">
                {tabs.map((t) => {
                    const isActive = t.key === activeTab;
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => t.enabled && onChange(t.key)}
                            disabled={!t.enabled}
                            title={t.enabled ? t.label : `${t.label} — coming soon`}
                            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                                isActive
                                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-300'
                                    : t.enabled
                                      ? 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
                                      : 'border-transparent text-gray-400 dark:text-gray-600 cursor-not-allowed'
                            }`}
                        >
                            <Icon size={13} />
                            <span>{t.label}</span>
                            {t.badgeCount != null && t.badgeCount > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-[10px] font-semibold">
                                    {t.badgeCount}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ---- LOG tab ---------------------------------------------------------
function LogTabContent({ detail }: { detail: AdminTaskDetailResponse }) {
    return (
        <>
            <section className="grid grid-cols-2 gap-3 text-xs">
                <InfoRow label="Engineer" value={detail.task.assignedEngineerName || 'Unassigned'} />
                <InfoRow label="Category" value={detail.task.category || '—'} />
                <InfoRow label="Planned"  value={`${formatDate(detail.task.plannedStart)} → ${formatDate(detail.task.plannedFinish)}`} />
                <InfoRow label="Actual"   value={`${formatDate(detail.task.actualStart)} → ${formatDate(detail.task.actualFinish)}`} />
                <InfoRow label="Progress" value={`${detail.task.progressPercent}%`} />
                {detail.task.quantity > 0 && (
                    <InfoRow
                        label="Quantity"
                        value={`${detail.task.quantityCompleted.toFixed(0)} / ${detail.task.quantity.toFixed(0)} ${detail.task.uom || ''}`}
                    />
                )}
            </section>

            {detail.task.status === 'On Hold' && detail.task.currentHoldNotes && (
                <div className="text-xs text-gray-600 dark:text-gray-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2">
                    <span className="font-medium text-amber-800 dark:text-amber-200">Current hold note:</span>{' '}
                    {detail.task.currentHoldNotes}
                </div>
            )}

            <HoldHistorySection episodes={detail.holdEpisodes} />

            <section>
                <h4 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
                    History ({detail.history.length})
                </h4>
                {detail.history.length === 0 ? (
                    <div className="text-xs text-gray-400">No updates yet.</div>
                ) : (
                    <ul className="space-y-2">
                        {detail.history.map((h) => (
                            <li
                                key={h.id}
                                className={`text-xs border-l-2 pl-3 ${
                                    h.hold_reason
                                        ? 'border-amber-400 dark:border-amber-500'
                                        : 'border-indigo-300 dark:border-indigo-600'
                                }`}
                            >
                                <div className="text-gray-700 dark:text-gray-200">
                                    {h.status_change && <span className="font-medium">{h.status_change}</span>}
                                    {h.hold_reason && (
                                        <span className="ml-1 inline-flex items-center bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-1.5 rounded text-[11px]">
                                            <PauseCircle size={10} className="mr-0.5" />
                                            {h.hold_reason}
                                        </span>
                                    )}
                                    {h.progress_percent != null && <span> · {h.progress_percent}%</span>}
                                    {h.quantity_completed != null && <span> · qty {Number(h.quantity_completed).toFixed(0)}</span>}
                                </div>
                                {(h.hold_notes || h.notes) && (
                                    <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                                        {h.hold_notes || h.notes}
                                    </div>
                                )}
                                <div className="text-gray-400 mt-0.5">
                                    {formatTimestamp(h.reported_at)} · {h.reported_by_name}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </>
    );
}

// ---- ISSUES tab ------------------------------------------------------
// OPEN/CLOSED sub-tabs mirror the mobile screen. "Report issue" lives in
// a modal attached to this tab; resolving/reopening an issue PATCHes the
// status directly inline.
function IssuesTabContent({
    detail,
    onReload,
}: {
    detail: AdminTaskDetailResponse;
    onReload: () => Promise<void>;
}) {
    const [sub, setSub] = useState<'OPEN' | 'CLOSED'>('OPEN');
    const [showCreate, setShowCreate] = useState(false);
    const [busyIssueId, setBusyIssueId] = useState<number | null>(null);

    const openItems   = detail.issues.filter((i) => i.status === 'open' || i.status === 'acknowledged');
    const closedItems = detail.issues.filter((i) => i.status === 'resolved' || i.status === 'closed');
    const items = sub === 'OPEN' ? openItems : closedItems;

    const handleTransition = async (issue: AdminTaskIssue, nextStatus: IssueStatus) => {
        setBusyIssueId(issue.id);
        try {
            await api.adminUpdateIssueStatus(issue.id, { status: nextStatus });
            await onReload();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to update issue');
        } finally {
            setBusyIssueId(null);
        }
    };

    return (
        <>
            <div className="flex items-center justify-between">
                {/* Sub-tab pill — keeps room for future filters (e.g. priority). */}
                <div className="inline-flex rounded-md bg-gray-100 dark:bg-slate-700 p-0.5 text-xs">
                    <button
                        type="button"
                        onClick={() => setSub('OPEN')}
                        className={`px-2.5 py-1 rounded ${
                            sub === 'OPEN'
                                ? 'bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 shadow-sm'
                                : 'text-gray-600 dark:text-gray-300'
                        }`}
                    >
                        Open ({openItems.length})
                    </button>
                    <button
                        type="button"
                        onClick={() => setSub('CLOSED')}
                        className={`px-2.5 py-1 rounded ${
                            sub === 'CLOSED'
                                ? 'bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 shadow-sm'
                                : 'text-gray-600 dark:text-gray-300'
                        }`}
                    >
                        Closed ({closedItems.length})
                    </button>
                </div>

                <button
                    type="button"
                    onClick={() => setShowCreate(true)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium"
                >
                    <AlertTriangle size={12} />
                    Report issue
                </button>
            </div>

            <div className="mt-3">
                {items.length === 0 ? (
                    <div className="text-xs text-gray-400 py-8 text-center">
                        {sub === 'OPEN' ? 'No open issues.' : 'No resolved issues yet.'}
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {items.map((i) => (
                            <IssueCard
                                key={i.id}
                                issue={i}
                                busy={busyIssueId === i.id}
                                onTransition={(s) => handleTransition(i, s)}
                            />
                        ))}
                    </ul>
                )}
            </div>

            {showCreate && (
                <CreateIssueModal
                    taskId={detail.task.id}
                    taskName={detail.task.name}
                    onClose={() => setShowCreate(false)}
                    onCreated={async () => {
                        setShowCreate(false);
                        await onReload();
                    }}
                />
            )}
        </>
    );
}

function IssueCard({
    issue,
    busy,
    onTransition,
}: {
    issue: AdminTaskIssue;
    busy: boolean;
    onTransition: (s: IssueStatus) => void;
}) {
    const isOpen = issue.status === 'open' || issue.status === 'acknowledged';
    return (
        <li className="text-xs border border-gray-200 dark:border-slate-700 rounded p-2.5">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-800 dark:text-gray-100 truncate">
                        {issue.title}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${getPriorityStyle(issue.priority)}`}>
                            {issue.priority}
                        </span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${getIssueStatusStyle(issue.status)}`}>
                            {issue.status}
                        </span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {prettifyConcernType(issue.concern_type)}
                        </span>
                    </div>
                </div>
            </div>
            {issue.description && (
                <div className="mt-1 text-gray-500 dark:text-gray-400">{issue.description}</div>
            )}
            <div className="mt-1.5 flex items-center justify-between">
                <div className="text-gray-400 text-[11px]">
                    {formatTimestamp(issue.created_at)}
                    {issue.reported_by_name && <> · {issue.reported_by_name}</>}
                </div>
                <div className="flex items-center gap-1">
                    {isOpen ? (
                        <>
                            {issue.status === 'open' && (
                                <IssueActionBtn label="Acknowledge" busy={busy} onClick={() => onTransition('acknowledged')} />
                            )}
                            <IssueActionBtn label="Resolve" busy={busy} onClick={() => onTransition('resolved')} primary />
                        </>
                    ) : (
                        <IssueActionBtn label="Reopen" busy={busy} onClick={() => onTransition('open')} />
                    )}
                </div>
            </div>
        </li>
    );
}

function IssueActionBtn({
    label,
    busy,
    onClick,
    primary = false,
}: {
    label: string;
    busy: boolean;
    onClick: () => void;
    primary?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={busy}
            className={`text-[11px] px-1.5 py-0.5 rounded font-medium disabled:opacity-40 ${
                primary
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600'
            }`}
        >
            {label}
        </button>
    );
}

function CreateIssueModal({
    taskId,
    taskName,
    onClose,
    onCreated,
}: {
    taskId: string;
    taskName: string;
    onClose: () => void;
    onCreated: () => Promise<void>;
}) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [concernType, setConcernType] = useState<IssueConcernType>('other');
    const [priority, setPriority] = useState<IssuePriority>('medium');
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!title.trim()) return;
        setSubmitting(true);
        try {
            await api.adminCreateTaskIssue(taskId, {
                title: title.trim(),
                description: description.trim() || undefined,
                concernType,
                priority,
            });
            await onCreated();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to create issue');
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md">
                <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Report issue</h3>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[340px]">{taskName}</p>
                    </div>
                    <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700">
                        <X size={16} />
                    </button>
                </header>

                <div className="p-4 space-y-3 text-xs">
                    <label className="block">
                        <span className="text-gray-600 dark:text-gray-300 font-medium">Title *</span>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Short summary"
                            className="mt-1 w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded text-sm text-gray-900 dark:text-gray-100"
                            autoFocus
                        />
                    </label>

                    <label className="block">
                        <span className="text-gray-600 dark:text-gray-300 font-medium">Description</span>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            placeholder="Optional detail"
                            className="mt-1 w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded text-sm text-gray-900 dark:text-gray-100"
                        />
                    </label>

                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-gray-600 dark:text-gray-300 font-medium">Type</span>
                            <select
                                value={concernType}
                                onChange={(e) => setConcernType(e.target.value as IssueConcernType)}
                                className="mt-1 w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded text-sm text-gray-900 dark:text-gray-100"
                            >
                                {ISSUE_CONCERN_TYPES.map((t) => (
                                    <option key={t} value={t}>{prettifyConcernType(t)}</option>
                                ))}
                            </select>
                        </label>

                        <label className="block">
                            <span className="text-gray-600 dark:text-gray-300 font-medium">Priority</span>
                            <select
                                value={priority}
                                onChange={(e) => setPriority(e.target.value as IssuePriority)}
                                className="mt-1 w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 rounded text-sm text-gray-900 dark:text-gray-100"
                            >
                                {ISSUE_PRIORITIES.map((p) => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>

                <footer className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-slate-700">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        className="px-3 py-1.5 text-xs rounded bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={submitting || !title.trim()}
                        className="px-3 py-1.5 text-xs rounded bg-indigo-600 hover:bg-indigo-700 text-white font-medium disabled:opacity-50"
                    >
                        {submitting ? 'Creating…' : 'Create issue'}
                    </button>
                </footer>
            </div>
        </div>
    );
}

function prettifyConcernType(t: IssueConcernType): string {
    return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function getPriorityStyle(p: IssuePriority): string {
    switch (p) {
        case 'critical': return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
        case 'high':     return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
        case 'medium':   return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
        case 'low':
        default:         return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300';
    }
}

function getIssueStatusStyle(s: IssueStatus): string {
    switch (s) {
        case 'open':         return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
        case 'acknowledged': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
        case 'resolved':     return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
        case 'closed':       return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300';
        default:             return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300';
    }
}

// ---- placeholder for wave-2 tabs ------------------------------------
function ComingSoonPlaceholder({ tab }: { tab: DrawerTabKey }) {
    const blurb: Record<DrawerTabKey, string> = {
        LOG:       '',
        ISSUES:    '',
        DOCS:      'Drawings and SOPs for this task will show here.',
        LABOUR:    'Contractors and daily worker attendance.',
        REQUESTS:  'Material requests and their approval status.',
        MATERIALS: 'Materials issued against this task.',
    };
    return (
        <div className="text-center py-12 px-4">
            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-300 mb-3">
                Coming soon
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-[260px] mx-auto">
                {blurb[tab]}
            </p>
        </div>
    );
}

// Full-day difference between two instants. We anchor both sides to local
// midnight so "paused yesterday, resumed today" reads as 1 day even when the
// exact times are only a few hours apart — matching how site managers talk
// about delays. An ongoing episode uses the current wall-clock time.
const MS_PER_DAY = 86_400_000;
function calendarDayDiff(startISO: string, endISO: string | null): number {
    const start = new Date(startISO);
    const end = endISO ? new Date(endISO) : new Date();
    const startMid = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const endMid   = new Date(end.getFullYear(),   end.getMonth(),   end.getDate()).getTime();
    return Math.max(0, Math.round((endMid - startMid) / MS_PER_DAY));
}

function formatDays(n: number): string {
    if (n <= 0) return '< 1 day';
    if (n === 1) return '1 day';
    return `${n} days`;
}

function HoldHistorySection({ episodes }: { episodes: AdminTaskHoldEpisode[] }) {
    // Order newest-first so the most recent pause is on top; this matches the
    // status drawer's existing "History" list ordering.
    const ordered = useMemo(
        () =>
            [...episodes].sort(
                (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
            ),
        [episodes],
    );

    // Per-episode days + totals grouped by hold_reason. We rebuild this in a
    // single pass so the KPI strip and the per-episode list stay consistent.
    const { episodeDays, totalsByReason, grandTotal, ongoingCount } = useMemo(() => {
        const days = new Map<string, number>();
        const byReason = new Map<string, { days: number; episodes: number; ongoing: boolean }>();
        let grand = 0;
        let ongoing = 0;
        for (const ep of ordered) {
            const d = calendarDayDiff(ep.started_at, ep.ended_at);
            days.set(ep.id, d);
            grand += d;
            if (ep.ongoing) ongoing += 1;
            const reasonKey = ep.hold_reason || 'Unspecified';
            const prev = byReason.get(reasonKey) || { days: 0, episodes: 0, ongoing: false };
            byReason.set(reasonKey, {
                days: prev.days + d,
                episodes: prev.episodes + 1,
                ongoing: prev.ongoing || ep.ongoing,
            });
        }
        return {
            episodeDays: days,
            totalsByReason: [...byReason.entries()].sort((a, b) => b[1].days - a[1].days),
            grandTotal: grand,
            ongoingCount: ongoing,
        };
    }, [ordered]);

    if (ordered.length === 0) return null;

    return (
        <section>
            <h4 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
                Hold history ({ordered.length})
            </h4>

            {/* Summary strip: total delay + per-reason breakdown. This is the
                planner's answer to "how much time did we lose and to what?" */}
            <div className="mb-3 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2.5">
                <div className="flex items-baseline justify-between">
                    <span className="text-[11px] uppercase tracking-wide text-amber-800 dark:text-amber-200 font-semibold">
                        Total delay
                    </span>
                    <span className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                        {formatDays(grandTotal)}
                        {ongoingCount > 0 && (
                            <span className="ml-1 text-[11px] font-normal text-amber-700 dark:text-amber-300">
                                (still on hold)
                            </span>
                        )}
                    </span>
                </div>
                {totalsByReason.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {totalsByReason.map(([reason, stats]) => (
                            <span
                                key={reason}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-white dark:bg-slate-800 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-700"
                                title={`${stats.episodes} episode${stats.episodes > 1 ? 's' : ''}`}
                            >
                                <PauseCircle size={10} />
                                <span className="font-medium">{reason}</span>
                                <span>· {formatDays(stats.days)}</span>
                                {stats.ongoing && <span className="text-amber-600">●</span>}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Per-episode list — shows each pause/resume with its reason and
                how long the task sat in that state. */}
            <ul className="space-y-2">
                {ordered.map((ep) => {
                    const days = episodeDays.get(ep.id) ?? 0;
                    return (
                        <li
                            key={ep.id}
                            className="text-xs border border-gray-200 dark:border-slate-700 rounded-md p-2.5"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 font-medium">
                                        <PauseCircle size={11} />
                                        {ep.hold_reason || 'Unspecified'}
                                    </span>
                                    {ep.ongoing && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                                            Ongoing
                                        </span>
                                    )}
                                </div>
                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                    {formatDays(days)}
                                </span>
                            </div>
                            <div className="mt-1 text-gray-500 dark:text-gray-400">
                                {formatTimestamp(ep.started_at)}
                                {' → '}
                                {ep.ended_at ? formatTimestamp(ep.ended_at) : 'now'}
                                {ep.ended_by_status && ep.ended_by_status !== 'On Hold' && (
                                    <span className="ml-1 text-gray-400">
                                        ({ep.ended_by_status === 'In Progress' ? 'Resumed' : ep.ended_by_status})
                                    </span>
                                )}
                            </div>
                            {ep.hold_notes && (
                                <div className="mt-1 text-gray-600 dark:text-gray-300 italic">
                                    "{ep.hold_notes}"
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <div className="text-gray-400 dark:text-gray-500 uppercase tracking-wide text-[10px]">{label}</div>
            <div className="text-gray-800 dark:text-gray-100">{value}</div>
        </div>
    );
}

// ----- helpers --------------------------------------------------------
// ---- card tab-status chips ------------------------------------------
// Compact version of the drawer's tab badges, so a planner can scan the
// listing and see which tasks have issues / docs / labour activity. Each
// chip is hidden when its count is zero so the card stays quiet for
// tasks without any sub-resource activity.
function TabStatusChips({ task }: { task: AdminTask }) {
    const chips: { key: string; label: string; count: number; tone: 'amber' | 'indigo' | 'slate' }[] = [];

    if (task.openIssuesCount > 0) {
        chips.push({
            key: 'issues',
            label: 'Issues',
            count: task.openIssuesCount,
            tone: 'amber',
        });
    }

    const docCount = task.linkedDocsCount ?? 0;
    if (docCount > 0) {
        chips.push({
            key: 'docs',
            label: 'Docs',
            count: docCount,
            tone: 'indigo',
        });
    }

    const matOpen = task.openMaterialRequestsCount ?? 0;
    if (matOpen > 0) {
        chips.push({
            key: 'requests',
            label: 'Mat.',
            count: matOpen,
            tone: 'slate',
        });
    }

    const labourN = task.labourHeadcountToday ?? 0;
    if (labourN > 0) {
        chips.push({
            key: 'labour',
            label: 'Labour',
            count: labourN,
            tone: 'slate',
        });
    }

    if (chips.length === 0) return null;

    return (
        <div className="flex flex-wrap justify-end gap-1">
            {chips.map((c) => (
                <span
                    key={c.key}
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${tabChipToneClass(c.tone)}`}
                >
                    {c.label} ({c.count})
                </span>
            ))}
        </div>
    );
}

function tabChipToneClass(tone: 'amber' | 'indigo' | 'slate'): string {
    switch (tone) {
        case 'amber':  return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
        case 'indigo': return 'bg-gray-200 text-gray-700 dark:bg-neutral-800 dark:text-neutral-200';
        case 'slate':
        default:       return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300';
    }
}

function getStatusStyle(s: TaskStatus): string {
    switch (s) {
        case 'In Progress': return 'bg-gray-200 text-gray-700 dark:bg-neutral-800 dark:text-neutral-200';
        case 'Completed':   return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
        case 'On Hold':     return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
        case 'Not Started':
        default:            return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300';
    }
}

function formatDate(d: string | null): string {
    if (!d) return '—';
    // Keep short so two dates fit on one line in the list row.
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatTimestamp(d: string): string {
    return new Date(d).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}
